<template>
  <AppFormGroup>
    <SubHeading keypath="headings.personal" />

    <div class="flex flex-col lg:flex-row lg:gap-4">
      <AppField
        v-model="maritalStatus"
        :path-prefix="applicantType"
        path="maritalStatus"
        class="grow"
        :required="maritalStatusRequired"
      />
    </div>

    <div
      v-if="maritalStatus === 'married'"
      class="flex flex-col lg:flex-row lg:gap-4"
    >
      <AppField
        :path-prefix="applicantType"
        path="spouse.firstName"
        class="grow"
        required
      />

      <AppField
        :path-prefix="applicantType"
        path="spouse.lastName"
        class="grow"
        required
      />
    </div>
  </AppFormGroup>

  <AppFormGroup v-if="maritalStatus === 'married'" class="pt-6">
    <SubHeading keypath="headings.address" />

    <div class="flex flex-col">
      <AppField
        v-model="livesWithSpouse"
        :path-prefix="applicantType"
        path="spouse.liveWithSpouse"
        class="-mb-8 pt-8"
      />
    </div>

    <div class="flex flex-col lg:flex-row lg:gap-x-4">
      <AppField
        :path-prefix="applicantType"
        :disabled="livesWithSpouse"
        path="spouse.homeAddress.line1"
        class="md:basis-3/4"
      />

      <AppField
        :path-prefix="applicantType"
        :disabled="livesWithSpouse"
        path="spouse.homeAddress.line2"
        class="md:basis-1/4"
      />
    </div>

    <div class="flex flex-col lg:flex-row lg:gap-x-4">
      <AppField
        :path-prefix="applicantType"
        :disabled="livesWithSpouse"
        path="spouse.homeAddress.city"
        class="md:basis-3/5"
      />

      <AppField
        :path-prefix="applicantType"
        :disabled="livesWithSpouse"
        path="spouse.homeAddress.state"
        class="md:basis-1/5"
      />

      <AppField
        :path-prefix="applicantType"
        :disabled="livesWithSpouse"
        path="spouse.homeAddress.zipCode"
        class="md:basis-1/5"
      />
    </div>
  </AppFormGroup>

  <AppFormGroup v-if="maritalStatus === 'married'" class="pt-6">
    <SubHeading keypath="headings.contact" />

    <div class="flex flex-col lg:flex-row lg:gap-4">
      <AppField :path-prefix="applicantType" path="spouse.email" class="grow" />
    </div>
  </AppFormGroup>
</template>

<script setup lang="ts">
  import {
    AppField,
    AppFormGroup,
    useAppFieldModel,
  } from "@versatile-credit-apply/components/form"

  import type {
    Address,
    ApplicantMaritalStatus,
    Spouse,
  } from "@versatile-credit-apply/omni-apply-client"
  import { useProviderI18n } from "@versatile-credit-apply/sdk"
  import { computed, ref, watch } from "vue"
  import type { FormSectionProps } from "./form-section-api"

  const props = defineProps<FormSectionProps>()

  useProviderI18n(() => props.providerKey)

  const maritalStatus = ref<ApplicantMaritalStatus>()

  const livesWithSpouse = ref<boolean>()

  const homeAddress = useAppFieldModel<Address | undefined>(
    () => `${props.applicantType}.homeAddress`
  )

  const spouse = useAppFieldModel<Spouse | undefined>(
    () => `${props.applicantType}.spouse`
  )

  const spouseAddress = useAppFieldModel<Address | undefined>(
    () => `${props.applicantType}.spouse.homeAddress`
  )

  const maritalStatusRequired = computed(() =>
    homeAddress?.value?.state === "WI" ? true : undefined
  )

  watch(livesWithSpouse, (cohabitates) => {
    if (cohabitates && homeAddress.value) {
      spouseAddress.value = {
        ...homeAddress.value,
      }
    } else {
      spouseAddress.value = undefined
    }
  })

  watch(maritalStatus, () => {
    spouse.value = undefined
  })
</script>

<i18n lang="yaml" locale="en">
appFields:
  city:
    label: Spouse City
  firstName:
    label: Spouse First Name
  lastName:
    label: Spouse Last Name
  line1:
    label: Spouse Street Address
  liveWithSpouse:
    label: Same Address?
  maritalStatus:
    label: Marital Status
  state:
    label: Spouse State
  zipCode:
    label: Spouse Postal Code
headings:
  address: Address Information
  contact: Contact Information
  personal: Personal Information
</i18n>
